<template>
  <div
    class="pb-2 d-flex-center"
    style="min-width: 1080px;"
  >
    <div
      v-if="stateLoading || !bookingData || stateLoadingCategory"
      class="text-center py-2 mt-2 mt-lg-4"
    >
      <p class="font-weight-bolder text-airline">
        {{ $t('reservation.loading2') }}
      </p>
      <div class="text-center">
        <b-spinner variant="info" />
      </div>
    </div>

    <template v-else>
      <div
        v-if="bookingData && !isEmpty(listTrips)"
        style="max-width: 1200px"
      >
        <TrainTicketConfig
          v-if="!['CANCEL'].includes(bookingData.status)"
          :config="config"
        />
        <TrainTicketAlertWarning />
        <vue-html2pdf
          ref="html2Pdf"
          :show-layout="false"
          :float-layout="false"
          :enable-download="true"
          :preview-modal="!isMobileView"
          :paginate-elements-by-height="1400"
          :manual-pagination="false"
          :html-to-pdf-options="pdfOptions"
          pdf-content-width="100%"
          style="min-width: 1080px;"
        >
          <section slot="pdf-content">
            <ElectronicTicketTrain
              :from-ticket-data="bookingData"
              :config.sync="config"
              :listTrips="listTrips"
              :passengerTargetList="getListPassengerTarget"
              @update:pricingInfo="$event => pricingInfo=$event"
            />
          </section>
        </vue-html2pdf>
        <div class="d-flex-center gap-3 py-1">
          <b-button
            v-ripple.400="'rgba(234, 84, 85, 0.15)'"
            variant="outline-danger"
            pill
            @click="handleCloseTab"
          >
            {{ $t('close') }} tab
          </b-button>
          <b-button
            v-if="!['CANCEL'].includes(bookingData.status)"
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            class="btn-gradient width-150 mt-lg-25"
            pill
            @click="openModalRename('img')"
          >
            {{ $t('flight.exportIMG') }}
          </b-button>
          <b-button
            v-if="!['CANCEL'].includes(bookingData.status)"
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            class="btn-gradient width-150 mt-lg-25"
            pill
            @click="openModalRename('pdf')"
          >
            {{ $t('flight.exportPDF') }}
          </b-button>
        </div>
      </div>
    </template>

    <!--ANCHOR - MODAL - modal-rename-file -->
    <b-modal
      id="modal-rename-file"
      header-bg-variant="light-info"
      centered
    >
      <template #modal-header>
        <h4 class="text-heading-3 m-0">
          {{ $t('flight.renameExport') }}
        </h4>
      </template>
      <IAmOverlay :loading="loadingToExport">
        <b-form-group label-for="filename">
          <template #label>
            <div class="text-heading-5">
              {{ $t('flight.filename') }}
              <span class="text-danger">(*)</span>
            </div>
          </template>
          <b-form-input
            id="filename"
            v-model="fileNameToCustom"
            :placeholder="$t('flight.filenamePlaceholder')"
            trim
          />
        </b-form-group>
      </IAmOverlay>
      <template #modal-footer>
        <b-button
          variant="outline-danger"
          pill
          @click="cancelHandle"
        >
          {{ $t('cancel') }}
        </b-button>
        <b-button
          variant="gradient"
          pill
          :disabled="!fileNameToCustom"
          @click="exportHandle(typeToExport)"
        >
          {{ $t(`flight.export${typeToExport.toUpperCase()}`) }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BSpinner, BButton, BModal, BFormInput, BFormGroup,
} from 'bootstrap-vue'
import { ref, watch } from '@vue/composition-api'
import html2canvas from 'html2canvas-pro'
import jsPDF from 'jspdf'
import isEmpty from 'lodash/isEmpty'

import router from '@/router'
import useGetTrainTicketConfig from '@/views/apps/configs/ticket-train/useGetTrainTicketConfig'
import { formatCurrency } from '@/@core/utils/filter'

import useTrainTicketFaceHandle from './useTrainTicketFaceHandle'

export default {
  components: {
    BSpinner,
    BButton,
    BModal,
    BFormInput,
    BFormGroup,

    IAmOverlay: () => import('@/components/IAmOverlay.vue'),
    VueHtml2pdf: () => import('vue-html2pdf'),

    TrainTicketConfig: () => import('@/views/apps/configs/ticket-train/components/TrainTicketConfig.vue'),
    TrainTicketAlertWarning: () => import('@/views/apps/configs/ticket-train/components/TrainTicketAlertWarning.vue'),
    ElectronicTicketTrain: () => import('@/views/apps/configs/ticket-train/ElectronicTicketTrain.vue'),
  },
  setup() {
    const {
      stateLoading,
      bookingData,
      // disabledCopy,
      // copyText,
      getBookingById,
      stateLoadingCategory,
      getListPassengerTarget,
    } = useTrainTicketFaceHandle()

    const {
      config,
      getTicketConfig,
    } = useGetTrainTicketConfig()

    const timeHold = ref(null)
    const flights = ref(null)
    const paxs = ref(null)
    const listTrips = ref(null)

    function onLoadTicketData() {
      getTicketConfig()
      // setTicketsData()
    }

    if (router.currentRoute.params.id) {
      getBookingById(router.currentRoute.params.id)
        .then(() => {
          onLoadTicketData()
        })
    }

    const IMAGE_SCALE = 2
    const pdfOptions = {
      margin: [0, 0],
      filename: 'ticket.pdf',
      image: { type: 'jpeg', quality: IMAGE_SCALE },
      html2canvas: { useCORS: true, scale: IMAGE_SCALE },
      jsPDF: {
        unit: 'in',
        format: 'a3',
        orientation: 'portrait',
      },
      pagebreak: { mode: 'avoid-all', after: '.avoidThisRow' },
    }
    const html2Pdf = ref(null)
    const fileNameToCustom = ref()

    watch(() => bookingData.value, booking => {
      if (!booking) return
      listTrips.value = booking.listTrip
      if (!fileNameToCustom.value) {
        fileNameToCustom.value = `${booking.bookingCode}_${booking.customerName}`
      }
    })

    const pricingInfo = ref({})

    const typeToExport = ref()
    const html2canvasOptions = {
      scrollX: 0,
      scrollY: -window.scrollY,
    }

    const exportToImage = async () => {
      const element = html2Pdf.value.$el.querySelector('section.content-wrapper')
      const inputMoneyDiv = element.querySelectorAll('#div-value-money')
      const inputMoneyDivClone = []
      inputMoneyDiv.forEach(el => {
        const firstChild = el.firstChild
        inputMoneyDivClone.push(firstChild.cloneNode(true))
        const newDiv = document.createElement('div')
        newDiv.textContent = formatCurrency(el.firstChild.defaultValue)
        newDiv.className = firstChild.className.replaceAll('\n', ' ').replace(/\s+/g, ' ')
        newDiv.classList.add('w-100')
        el.replaceChildren(newDiv)
      })
      const canvas = await html2canvas(element, {
        ...html2canvasOptions,
      })
        .finally(() => {
          inputMoneyDiv.forEach((el, index) => {
            el.replaceChildren(inputMoneyDivClone[index])
          })
        })
      const image = canvas.toDataURL('image/png', {
        allowTaint: false,
        useCORS: true,
      })
      const link = document.createElement('a')
      link.href = image
      const fileName = `${fileNameToCustom.value}.png`
      link.download = fileName
      link.click()
    }

    function exportToPDF() {
      const element = html2Pdf.value.$el.querySelector('section.content-wrapper')
      const inputMoneyDiv = element.querySelectorAll('#div-value-money')
      const inputMoneyDivClone = []
      inputMoneyDiv.forEach(el => {
        const firstChild = el.firstChild
        inputMoneyDivClone.push(firstChild.cloneNode(true))
        const newDiv = document.createElement('div')
        newDiv.textContent = formatCurrency(el.firstChild.defaultValue)
        newDiv.className = firstChild.className.replaceAll('\n', ' ').replace(/\s+/g, ' ')
        newDiv.classList.add('w-100')
        el.replaceChildren(newDiv)
      })
      const canvasDivPdf = element
      const margin = 20
      html2canvas(canvasDivPdf, {
        // allowTaint: true,
        ...html2canvasOptions,
        ...(pdfOptions.html2canvas ?? {}),
      })
        .then(canvas => {
          const canvasHeight = canvas.height / IMAGE_SCALE
          const canvasWidth = canvas.width / IMAGE_SCALE
          const pdfHeight = canvasHeight < canvasWidth ? canvasWidth : canvasHeight
          canvas.getContext('2d')
          const imgData = canvas.toDataURL('image/png', 1.0)
          // eslint-disable-next-line new-cap
          const pdf = new jsPDF('p', 'pt', [pdfHeight + (margin * 2), canvasWidth + (margin * 2)], true)
          pdf.addImage(imgData, 'PNG', margin, margin, canvasWidth, canvasHeight)
          const FileName = `${fileNameToCustom.value}.pdf`
          pdf.save(FileName)
        })
        .finally(() => {
          inputMoneyDiv.forEach((el, index) => {
            el.replaceChildren(inputMoneyDivClone[index])
          })
        })
    }

    function openModalRename(type) {
      typeToExport.value = type
      this.$bvModal.show('modal-rename-file')
    }

    function handleCloseTab() {
      window.close()
    }

    function cancelHandle() {
      this.$bvModal.hide('modal-rename-file')
    }

    const loadingToExport = ref(false)
    function exportHandle(type) {
      loadingToExport.value = true
      if (type === 'img') exportToImage()
      if (type === 'pdf') exportToPDF()
      setTimeout(() => {
        this.$bvModal.hide('modal-rename-file')
        loadingToExport.value = false
      }, 1000)
    }

    return {
      bookingData,
      stateLoading,

      config,

      pdfOptions,
      timeHold,
      flights,
      paxs,

      listTrips,
      stateLoadingCategory,
      getListPassengerTarget,

      // export ticket to image
      html2Pdf,
      exportToImage,
      exportToPDF,

      // export ticket to pdf
      pricingInfo,

      isEmpty,
      handleCloseTab,
      openModalRename,
      cancelHandle,
      exportHandle,
      typeToExport,
      fileNameToCustom,
      loadingToExport,
    }
  },
  watch: {
    $route(to) {
      this.getBookingById(to.params.id)
    },
  },
}
</script>
